import { useCallback, useMemo, useRef, useState } from 'react'
import type { RightPanelRequest, RightPanelValue } from './rightPanelContext'

const STORAGE_KEY = 'hop-right-panel-expanded'

const readStored = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== 'false'
  } catch {
    return true
  }
}

const persist = (expanded: boolean) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(expanded))
  } catch {
    // Private browsing or full storage: the choice simply lasts for the session.
  }
}

const useRightPanelState = () => {
  const [expanded, setExpanded] = useState(readStored)
  const [request, setRequest] = useState<RightPanelRequest>(null)
  const [handledRequestId, setHandledRequestId] = useState<number | null>(null)
  const [notifications, setNotifications] = useState<Record<string, number>>({})
  const requestCounter = useRef(0)

  const toggle = useCallback(() => {
    const next = !expanded
    setExpanded(next)
    persist(next)
  }, [expanded])

  const openModule = useCallback((key: string) => {
    setExpanded(true)
    persist(true)
    requestCounter.current += 1
    setRequest({ key, id: requestCounter.current })
  }, [])

  const markRequestHandled = useCallback((id: number) => setHandledRequestId(id), [])

  const setNotification = useCallback((key: string, count: number) => {
    setNotifications(previous => (previous[key] === count ? previous : { ...previous, [key]: count }))
  }, [])

  const panel: RightPanelValue = useMemo(
    () => ({ expanded, toggle, openModule, request, handledRequestId, markRequestHandled, setNotification }),
    [expanded, toggle, openModule, request, handledRequestId, markRequestHandled, setNotification],
  )

  return { panel, notifications }
}

export default useRightPanelState
